import { useState, useEffect } from 'react';
import * as Sentry from "@sentry/react";

// ----------------------------------------------------------------------

export default function useLocalStorage<ValueType>(key: string, defaultValue: ValueType) {
  const [value, setValue] = useState(() => {
    let storedValue = null;
    try {
      storedValue = localStorage.getItem(key);
    } catch (e) {
      Sentry.captureException(e);
    }

    return storedValue === null ? defaultValue : JSON.parse(storedValue);
  });

  useEffect(() => {
    const listener = (e: StorageEvent) => {
      if (e.storageArea === localStorage && e.key === key) {
        setValue(e.newValue ? JSON.parse(e.newValue) : e.newValue);
      }
    };
    window.addEventListener('storage', listener);

    return () => {
      window.removeEventListener('storage', listener);
    };
  }, [key, defaultValue]);

  const setValueInLocalStorage = (newValue: ValueType) => {
    setValue((currentValue: ValueType) => {
      const result = typeof newValue === 'function' ? newValue(currentValue) : newValue;

      try {
        localStorage.setItem(key, JSON.stringify(result));
      } catch (e) {
        Sentry.captureException(e);
      }

      return result;
    });
  };

  return [value, setValueInLocalStorage];
}
